import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useAddRecipeIngredient } from "../../../../api/recipeSimulator";
import { RecipeIngredientType } from "../../../../config/enums/IngredientType.enum";
import { pickNextColor } from "../../../../utils/recipeColor.utill";
import {
  buildIngredientSubmitDto,
  canRemoveRow,
  createEmptyDraftRow,
  isRowSaveable
} from "../helpers/recipeAddIngredient.helpers";
import {
  CalculatedIngredientItem,
  CalculatedProductSalesUnitItem,
  GenericIngredientItem,
  GenericIngredientOption,
  IngredientDraftFormValues,
  ProductUnitOption
} from "../types/recipeAddIngredient.types";
import { buildInitialDraftRows } from "../utils/existingIngredients.utils";
import {
  calculatePendingDraftTotal,
  calculateRemainingCapacity,
  calculateSavedDraftTotal,
  clampPercentage,
  getMaxAllowedForRow
} from "../utils/recipeAddIngredient.utils";

interface UseRecipeAddIngredientFormParams {
  visibleModal: boolean;
  recipeId: string;
  recipeDataRefetch: () => void;
  usedColors: string[];
  calculatedProductSalesUnit: CalculatedProductSalesUnitItem[];
  genericIngredient: GenericIngredientItem[];
  calculatedIngredient: CalculatedIngredientItem[];
  productUnitOptions: ProductUnitOption[];
  genericOptions: GenericIngredientOption[];
}

export function useRecipeAddIngredientForm({
  visibleModal,
  recipeId,
  recipeDataRefetch,
  usedColors,
  calculatedProductSalesUnit,
  genericIngredient,
  calculatedIngredient,
  productUnitOptions,
  genericOptions
}: UseRecipeAddIngredientFormParams) {
  const form = useForm<IngredientDraftFormValues>({
    defaultValues: { drafts: [createEmptyDraftRow()] },
    mode: "onChange"
  });

  const { control, trigger, getValues, setValue, reset } = form;
  const { fields, append, remove, update } = useFieldArray({ control, name: "drafts" });
  const drafts = useWatch({ control, name: "drafts" }) ?? [];

  const addIngredientMutation = useAddRecipeIngredient();
  const wasVisibleRef = useRef(false);

  useEffect(() => {
    if (!visibleModal) {
      wasVisibleRef.current = false;
      return;
    }

    if (wasVisibleRef.current) {
      return;
    }

    wasVisibleRef.current = true;
    reset({
      drafts: buildInitialDraftRows(
        calculatedProductSalesUnit,
        genericIngredient,
        calculatedIngredient,
        createEmptyDraftRow,
        genericOptions
      )
    });
  }, [
    visibleModal,
    reset,
    calculatedProductSalesUnit,
    genericIngredient,
    calculatedIngredient,
    genericOptions
  ]);

  const totalAllocated = useMemo(
    () => drafts.reduce((sum, draft) => sum + (Number(draft.percentage) || 0), 0),
    [drafts]
  );

  const savedTotal = useMemo(
    () =>
      drafts
        .filter(draft => draft.saved && !draft.isExisting)
        .reduce((sum, draft) => sum + (Number(draft.percentage) || 0), 0),
    [drafts]
  );
  const pendingTotal = useMemo(() => calculatePendingDraftTotal(drafts), [drafts]);
  const remaining = useMemo(() => calculateRemainingCapacity(totalAllocated, 0), [totalAllocated]);

  const hasPending = useMemo(() => drafts.some(draft => !draft.saved), [drafts]);
  const hasSaved = useMemo(() => drafts.some(draft => draft.saved), [drafts]);
  const pendingCount = useMemo(() => drafts.filter(draft => !draft.saved).length, [drafts]);

  const resetForm = useCallback(() => {
    reset({
      drafts: buildInitialDraftRows(
        calculatedProductSalesUnit,
        genericIngredient,
        calculatedIngredient,
        createEmptyDraftRow,
        genericOptions
      )
    });
  }, [
    reset,
    calculatedProductSalesUnit,
    genericIngredient,
    calculatedIngredient,
    genericOptions
  ]);

  const handleIngredientTypeChange = useCallback(
    (index: number, value: string) => {
      setValue(`drafts.${index}.ingredientType`, value as RecipeIngredientType | "");
      setValue(`drafts.${index}.productUnitKey`, "");
      setValue(`drafts.${index}.ingredientId`, "");
      setValue(`drafts.${index}.descriptionOpen`, false);
    },
    [setValue]
  );

  const handlePercentageChange = useCallback(
    (index: number, value: string) => {
      if (value === "") {
        setValue(`drafts.${index}.percentage`, "");
        return;
      }
      const num = parseFloat(value);
      if (isNaN(num) || num < 0) {
        return;
      }
      const maxAllowed = getMaxAllowedForRow(drafts, index);
      if (num > maxAllowed) {
        return; // Don't allow value greater than max
      }
      // Keep the string value as-is to allow typing decimals
      setValue(`drafts.${index}.percentage`, value);
    },
    [drafts, setValue]
  );


  

  const saveRow = useCallback(
    async (index: number, addNewAfterSave = false) => {
      let draft = getValues(`drafts.${index}`);
      if (!draft || draft.isExisting) return;

      if (draft.percentage === "") {
        setValue(`drafts.${index}.percentage`, 0);
        draft = { ...draft, percentage: 0 };
      }

      const isValid = await trigger(`drafts.${index}`);
      if (!isValid) return;

      if (!isRowSaveable(draft)) return;

      const submitData = buildIngredientSubmitDto(draft, productUnitOptions, genericOptions);
      if (!submitData) return;

      let percentage = Number(draft.percentage) || 0;
      const maxAllowed = getMaxAllowedForRow(drafts, index);
      percentage = clampPercentage(percentage, maxAllowed);
      const displayName =
        "ingredientName" in submitData
          ? submitData.ingredientName
          : productUnitOptions.find(option => option.value === draft.productUnitKey)?.title;

      addIngredientMutation.mutate(
        {
          recipeId,
          type: draft.ingredientType as RecipeIngredientType,
          data: {
            ...submitData,
            percentage,
            chartColor: pickNextColor(usedColors)
          }
        },
        {
          onSuccess: () => {
            update(index, {
              ...draft,
              saved: true,
              descriptionOpen: false,
              displayName: displayName || draft.displayName
            });
            recipeDataRefetch();
            if (addNewAfterSave) {
              append(createEmptyDraftRow());
            }
          }
        }
      );
    },
    [
      getValues,
      setValue,
      trigger,
      productUnitOptions,
      genericOptions,
      addIngredientMutation,
      recipeId,
      usedColors,
      update,
      recipeDataRefetch,
      append
    ]
  );

  const saveRowAndAddNew = useCallback(
    (index: number) => {
      void saveRow(index, true);
    },
    [saveRow]
  );

  const removeRow = useCallback(
    (index: number) => {
      const draft = drafts[index];
      if (!draft || draft.isExisting || !canRemoveRow(draft, pendingCount, hasSaved)) return;
      remove(index);
      if (fields.length === 1) {
        append(createEmptyDraftRow());
      }
    },
    [drafts, pendingCount, hasSaved, remove, fields.length, append]
  );

  const addRow = useCallback(() => {
    if (hasPending) return;
    append(createEmptyDraftRow());
  }, [hasPending, append]);

  const setDescriptionOpen = useCallback(
    (index: number, open: boolean) => {
      setValue(`drafts.${index}.descriptionOpen`, open);
    },
    [setValue]
  );

  const isSaving = addIngredientMutation.isLoading;

  return {
    form,
    control,
    fields,
    drafts,
    hasPending,
    hasSaved,
    pendingCount,
    remaining,
    savedTotal,
    pendingTotal,
    isSaving,
    resetForm,
    handleIngredientTypeChange,
    handlePercentageChange,
    saveRow,
    saveRowAndAddNew,
    removeRow,
    addRow,
    setDescriptionOpen
  };
};
